import * as amplitude from "@amplitude/analytics-browser";
import { ApolloError, useApolloClient } from "@apollo/client";
import {
  Attributes,
  EvaluateRequestBuilder,
  FormEvaluateRequestBuilder,
} from "@avela/avela-authorization-sdk";
import {
  Alert,
  AlertIcon,
  Checkbox,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  List,
  ListItem,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { identity } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { RiAlertLine } from "react-icons/ri";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { StatusTag } from "src/components/DataDisplay/StatusTag";
import { ApolloError as ApolloErrorView } from "src/components/Feedback/ApolloError";
import { GenericError } from "src/components/Feedback/GenericError";
import { Loading } from "src/components/Feedback/Loading";
import { NotFound } from "src/components/Feedback/NotFound";
import { Section } from "src/components/Form/Section";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { FormStatusMenu } from "src/components/Menus/FormStatusMenu";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { FORM_AUTHORIZATION_SCOPE } from "src/components/Providers/AuthorizationProvider";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  FormContext,
  FormProvider,
  FormProviderFormTemplate,
  FormTemplateContext,
} from "src/components/Providers/FormProvider";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { Glossary } from "src/components/Text/Glossary";
import { AttendanceTypes, FORM_STATUS } from "src/constants";
import {
  createRuleExecutions,
  getFormAlertsByFormId,
  GetFormAlertsByFormIdResponse,
} from "src/generated/hooks/form-service/requests";
import { useAuthorizationApi } from "src/hooks/useAuthorizationApi";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useFormAnswers } from "src/hooks/useFormAnswers";
import { useFormTemplatePermissions } from "src/hooks/useFormTemplatePermissions";
import { useOrganization } from "src/hooks/useOrganization";
import { usePreviousFormSchools } from "src/hooks/usePreviousFormSchools";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useSchoolAdminPermissions } from "src/hooks/useSchoolAdminPermissions";
import useUser from "src/hooks/useUser";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { GET_FORM_ANSWERS_BY_ID } from "src/scenes/parent/forms/graphql/queries";
import * as breadcrumb from "src/services/breadcrumb";
import { formatIsoDateToOrgDate, fullName } from "src/services/format";
import * as FormTemplate from "src/services/formTemplate";
import { Status } from "src/types/authData";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { NextStepMenuSingle } from "../components/NextStepMenuSingle";
import { SetVisibilityMenuSingle } from "../components/SetVisibilityMenuSingle";
import {
  trackFormAction,
  trackFormActionError,
} from "../components/trackFormAction";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
  GET_FORM_VIEW_BY_ID,
  GET_PREVIOUS_FORM_BY_FORM_ID,
  GET_SCHOOLS_RANK_VIEW,
  GET_VERIFICATIONS_FOR_ADMIN,
} from "../graphql/queries";
import { useFormStatusUpdates } from "../hooks/useFormStatusUpdates";
import { VerificationSummary } from "../verifications/VerificationSummary";
import { ExportMenu } from "./ExportMenu";
import { FormAlerts } from "./FormAlerts/FormAlerts";
import { FormDetails } from "./FormDetails";
import { FormMessages } from "./FormMessages/FormMessages";
import { HistoryLog } from "./HistoryLog/HistoryLog";
import { RelatedForms } from "./RelatedForms";
import { SectionNavigation } from "./SectionNavigation";
import { StudentProfile } from "./StudentProfile";
import { CombinedData } from "./types";

type FormPermissionsState = {
  attributes: Attributes;
  formPermissionsBuilder: EvaluateRequestBuilder | null;
};

function handleError(error: Error | ApolloError): React.ReactElement {
  if (error instanceof ApolloError) {
    return <ApolloErrorView error={error} />;
  }

  return <GenericError />;
}

export const ViewForm: React.FC = () => {
  const { id } = useParams();

  if (!id) return <NotFound />;

  return (
    <FormProvider formId={id}>
      {(formTemplateRD, formRD) => {
        return (
          <InternalViewForm
            id={id}
            formTemplateContext={formTemplateRD}
            formRD={formRD}
          />
        );
      }}
    </FormProvider>
  );
};

type InternalViewFormProps = {
  id: string;
  formTemplateContext: RD.RemoteData<Error, FormTemplateContext>;
  formRD: RD.RemoteData<Error, FormContext>;
};

const InternalViewForm: React.FC<InternalViewFormProps> = ({
  id,
  formTemplateContext,
  formRD,
}) => {
  const user = useUser();
  const organization = useOrganization();
  const [searchParams] = useSearchParams();
  const { attributes } = useUserPermissions();
  const { evaluateWithPrincipalOverride } = useAuthorizationApi(
    FORM_AUTHORIZATION_SCOPE,
    id
  );
  const apolloClient = useApolloClient();
  const flags = useFlags([
    "school-admin-status-edit",
    "connect-form-rule-on-form-service",
  ]);

  // StudentForm creates a react-router Link with state as {showBirthdateWarning: true}
  // check this state and show the birthdate warning message
  const location = useLocation();
  const {
    isOpen: showBirthdateWarning,
    onClose,
    onOpen,
  } = useDisclosure({ defaultIsOpen: false });
  useEffect(() => {
    const state: any = location.state;
    if (state?.showBirthdateWarning) {
      onOpen();
    }
  }, [onOpen, location.state]);

  const toast = useAvelaToast();
  const { isSchoolAdmin, attendingSchoolIds } = useSchoolAdmin();
  const { formSchoolRankFilter } = useSchoolAdminPermissions();
  const { pruneFormTemplate } = useFormTemplatePermissions();
  const [editedSectionId, setEditedSectionId] = React.useState<
    string | undefined
  >(undefined);

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "No, cancel",
      },
      confirmButton: {
        label: "Yes, change status",
        colorScheme: "red",
      },
      translate: true,
    });

  const skipQueries =
    !id || !organization.hasData() || user.status !== Status.OK;

  const { remoteData: formViewByIdRD } = useRemoteDataQuery<
    GQL.GetFormViewById,
    GQL.GetFormViewByIdVariables
  >(GET_FORM_VIEW_BY_ID, {
    variables: {
      form_id: id ?? "",
      organization_id: organization.toNullable()?.id ?? "",
      is_admin: true,
    },
    skip: skipQueries,
  });

  const prunedFormTemplateRD = useMemo(
    () =>
      RD.toTuple(formViewByIdRD, formTemplateContext).map(([data, { gql }]) => {
        if (!data.form_by_pk) return gql;

        if (!gql) return undefined;

        const attendingSchoolId =
          data.form_by_pk.form_attending_school?.school?.id;
        const attendanceContext =
          attendingSchoolId && attendingSchoolIds.includes(attendingSchoolId)
            ? AttendanceTypes.Attending
            : AttendanceTypes.Applying;

        return pruneFormTemplate(gql, attendanceContext);
      }),
    [formViewByIdRD, formTemplateContext, attendingSchoolIds, pruneFormTemplate]
  );

  const { remoteData: previousFormData } = useRemoteDataQuery<
    GQL.GetPreviousFormByFormId,
    GQL.GetPreviousFormByFormIdVariables
  >(GET_PREVIOUS_FORM_BY_FORM_ID, {
    variables: {
      form_id: id ?? "",
    },
    skip: !id,
  });

  const formTemplate: FormProviderFormTemplate | undefined = useMemo(
    () =>
      prunedFormTemplateRD.hasData() ? prunedFormTemplateRD.data : undefined,
    [prunedFormTemplateRD]
  );

  const formById = useMemo(
    () =>
      formViewByIdRD.hasData() && formViewByIdRD.data.form_by_pk
        ? formViewByIdRD.data.form_by_pk
        : undefined,
    [formViewByIdRD]
  );

  const answersRemoteData = useFormAnswers(
    formById,
    formTemplate,
    id,
    skipQueries
  );

  const { remoteData: getSchoolsRankRemoteData, refetch: refetchSchoolsRank } =
    useRemoteDataQuery<GQL.GetSchoolsRankView, GQL.GetSchoolsRankViewVariables>(
      GET_SCHOOLS_RANK_VIEW,
      {
        variables: {
          form_id: id ?? "",
          skip_rank: isSchoolAdmin,
          enrollment_period_id: formTemplate?.enrollment_period_id ?? "",
          filter: formSchoolRankFilter(),
        },
        skip: skipQueries || !formTemplate?.enrollment_period_id,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-only",
      }
    );

  const { remoteData: verificationsRemoteData } = useRemoteDataQuery<
    GQL.GetVerificationsForAdmin,
    GQL.GetVerificationsForAdminVariables
  >(GET_VERIFICATIONS_FOR_ADMIN, {
    variables: { form_id: id ?? "" },
    skip: skipQueries,
  });

  const {
    remoteData: assignedFormsRemoteData,
    startPolling,
    stopPolling,
  } = useRemoteDataQuery<
    GQL.GetAssignedFormsByFormId,
    GQL.GetAssignedFormsByFormIdVariables
  >(GET_ASSIGNED_FORMS_BY_FORM_ID, {
    variables: { form_id: id ?? "" },
    skip: skipQueries,
    pollInterval: 10000,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    const interval = setInterval(() => {
      stopPolling();
    }, 600000); // @@@ WARNING - "STOP POLLING" MAY BE A PROBLEM FOR ADMINS
    return () => clearInterval(interval);
  }, [startPolling, stopPolling]);

  const previousFormSchoolIdsRemoteData = usePreviousFormSchools(id);

  const { updateFormStatus, isLoading } = useFormStatusUpdates();

  const [formPermissionsState, setFormPermissionsState] =
    React.useState<FormPermissionsState>({
      attributes: {},
      formPermissionsBuilder: null,
    });

  const formAlertsQuery = useQuery({
    queryKey: ["formAlerts", id],
    queryFn: () =>
      getFormAlertsByFormId({ path: { id: id } }).then((r) => r.data),
  });

  const executeFormRulesMutation = useMutation({
    mutationFn: () =>
      createRuleExecutions({
        body: { data: { targets: [{ type: "form", formId: id }] } },
      }),
    onSuccess: async () => {
      await formAlertsQuery.refetch();
    },
  });

  const formAlertsRD = RD.useFromRestResultMemoized(formAlertsQuery);

  const formData: RD.RemoteData<ApolloError | Error, CombinedData> =
    React.useMemo(
      () =>
        RD.toTuple5(
          RD.toTuple3(formViewByIdRD, prunedFormTemplateRD, formAlertsRD),
          getSchoolsRankRemoteData,
          answersRemoteData,
          verificationsRemoteData,
          RD.toTuple3(
            previousFormData,
            previousFormSchoolIdsRemoteData,
            assignedFormsRemoteData
          )
        )
          .mapError<ApolloError | Error>(identity)
          .andThen(
            ([
              [data, prunedFormTemplate, formAlerts],
              schoolsRank,
              answers,
              verificationSummary,
              [previousForm, previousFormSchoolIds, assignedForm],
            ]) => {
              if (!data.form_by_pk) {
                return RD.failure<ApolloError | Error, CombinedData>(
                  new Error()
                );
              }

              if (!prunedFormTemplate) {
                return RD.failure<ApolloError | Error, CombinedData>(
                  new Error()
                );
              }

              try {
                const formTemplate = FormTemplate.fromGQL(prunedFormTemplate);
                const remoteData = RD.success<
                  ApolloError | Error,
                  CombinedData
                >({
                  form: data.form_by_pk,
                  formTemplate: prunedFormTemplate,
                  previousForm: previousForm.form_by_pk,
                  sections: formTemplate.sections,
                  custom_question_answer_bank_relationships:
                    data.form_by_pk.custom_question_answer_bank_relationships,
                  schoolsRank: schoolsRank.form_school_rank,
                  tagGroups: schoolsRank.tag_group,
                  verificationSummary,
                  answers,
                  previousFormSchoolIds,
                  assignedForms: assignedForm.assigned_form,
                  formAlerts,
                });

                const formPermissionsBuilder = new FormEvaluateRequestBuilder({
                  formTemplateKey: formTemplate.key,
                  metadata: {
                    applyingSchoolIds: schoolsRank.form_school_rank.map(
                      (rank) => rank.school.id
                    ),
                    currentSchoolId:
                      data.form_by_pk.form_attending_school?.school?.id ??
                      "None",
                  },
                });

                for (const section of formTemplate.sections) {
                  if (!section) continue;
                  formPermissionsBuilder.buildFormSectionEdit({
                    sectionKey: section.key || section.id,
                    attributes: {
                      sectionType: section.type,
                    },
                  });
                  if (!("questions" in section)) continue;
                  for (const question of section.questions) {
                    formPermissionsBuilder.buildFormQuestionEdit(
                      {
                        sectionKey: section.key || section.id,
                        questionKey: question.key || question.id,
                        attributes: {
                          questionType: question.type,
                        },
                      },
                      {
                        sectionType: section.type,
                      }
                    );
                    // nested questions
                    if (
                      question.type === AF.MultiSelectType ||
                      question.type === AF.SingleSelectType
                    ) {
                      for (const option of question.options) {
                        if (!option.additionalQuestions) continue;
                        for (const additionalQuestion of option.additionalQuestions) {
                          formPermissionsBuilder.buildFormQuestionEdit(
                            {
                              sectionKey: section.key || section.id,
                              questionKey:
                                additionalQuestion.key || additionalQuestion.id,
                              attributes: {
                                questionType: additionalQuestion.type,
                              },
                            },
                            {
                              sectionType: section.type,
                            }
                          );
                        }
                      }
                    }
                  }
                }

                setFormPermissionsState({
                  attributes,
                  formPermissionsBuilder,
                });

                return remoteData;
              } catch (error: unknown) {
                console.error(error);
                return RD.failure<ApolloError | Error, CombinedData>(
                  error as Error
                );
              }
            }
          ),
      [
        formViewByIdRD,
        prunedFormTemplateRD,
        formAlertsRD,
        getSchoolsRankRemoteData,
        answersRemoteData,
        verificationsRemoteData,
        previousFormData,
        previousFormSchoolIdsRemoteData,
        assignedFormsRemoteData,
        attributes,
      ]
    );

  // evaluate the form permissions with principal override
  // outside of the initial render
  useEffect(() => {
    const { attributes, formPermissionsBuilder } = formPermissionsState;
    if (formPermissionsBuilder) {
      evaluateWithPrincipalOverride(attributes, formPermissionsBuilder);
    }
  }, [formPermissionsState, evaluateWithPrincipalOverride]);

  const handleOnSelectStatusChange = React.useCallback(
    async (value: GQL.form_status_enum) => {
      setHeader(
        <Text>
          <Glossary>{`${FORM_STATUS[value].statusDialogHeader}`}</Glossary>
        </Text>
      );
      setBody(
        <Text>
          <Glossary>{`${FORM_STATUS[value].statusDialogDescription}`}</Glossary>
        </Text>
      );

      amplitude.track("Clicked update form status", {
        form_id: id,
        status: value,
      });

      if (await confirm()) {
        amplitude.track("Confirmed update form status", {
          form_id: id,
          status: value,
        });

        try {
          if (!id) return;

          await updateFormStatus([id], value);
          await formAlertsQuery.refetch();
          await apolloClient.refetchQueries({
            include: [
              GET_FORM_VIEW_BY_ID,
              FETCH_OFFER_WAITLIST_STATUS,
              GET_SCHOOLS_RANK_VIEW,
              GET_ASSIGNED_FORMS_BY_FORM_ID,
            ],
          });

          toast({ title: "Form status updated" });
          trackFormAction({
            verb: "Completed",
            actionType: "SET_FORM_STATUS",
            bulk: false,
            count: 1,
            async: false,
            extra: { form_id: id, status: value },
          });
        } catch (err) {
          trackFormActionError(err, {
            actionType: "SET_FORM_STATUS",
            bulk: false,
            count: 1,
            async: false,
            extra: { form_id: id, status: value },
          });
          toast.error({ title: "Error updating form status" });
        }
      } else {
        amplitude.track("Canceled update form status", {
          form_id: id,
          status: value,
        });
      }
    },
    [
      setHeader,
      setBody,
      id,
      confirm,
      updateFormStatus,
      apolloClient,
      toast,
      formAlertsQuery,
    ]
  );

  const hasEditStatusPermissionAdmins = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.DISTRICT_ADMIN,
  ]);

  // hard-coding to allow full school admin edit permissions based on feature flag
  // TODO: remove when custom user roles allow for school admin to configure more granular form edit permissions
  const hasEditStatusPermissionSchoolAdmin =
    useRequiredHasuraRoles([HasuraRole.SCHOOL_ADMIN]) &&
    flags["school-admin-status-edit"].enabled;

  const hasEditStatusPermission =
    hasEditStatusPermissionAdmins || hasEditStatusPermissionSchoolAdmin;

  const [showHiddenQuestions, setShowHiddenQuestions] = useState(false);

  // Compute tab visibility
  const { hasSome: hasUserPermission } = useUserPermissions();
  const canViewMessages = hasUserPermission(["message:read"]);
  const canViewHistory =
    hasEditStatusPermissionAdmins && hasUserPermission(["form:read"]);
  const canViewAlerts =
    (hasEditStatusPermissionAdmins || isSchoolAdmin) &&
    hasUserPermission(["form:read"]);

  const visibleTabKeys = [
    "form",
    ...(canViewMessages ? ["messages"] : []),
    ...(canViewHistory ? ["history"] : []),
    ...(canViewAlerts ? ["alerts"] : []),
  ];
  const initialTabIndex = Math.max(
    visibleTabKeys.indexOf(searchParams.get("tab") ?? ""),
    0
  );

  if (!id) return <NotFound />;

  return (
    <RemoteDataView
      remoteData={RD.toTuple3(formData, prunedFormTemplateRD, formRD)}
      error={handleError}
      loading={<Loading />}
    >
      {([
        {
          form,
          previousForm,
          custom_question_answer_bank_relationships,
          sections,
          schoolsRank,
          tagGroups,
          verificationSummary,
          previousFormSchoolIds,
          assignedForms,
          formAlerts,
        },
        prunedFormTemplate,
        formContext,
      ]) => {
        const answers = formContext.gql;
        const form_answers = answers.form_answer;
        const grades_answers = answers.grades_answer;
        const address_answers = answers.form_address;
        const custom_question_answers = answers.custom_question_answer;
        const formDisclaimer = answers.form_disclaimer[0];
        const statusUpdatedDate = form.status_updated_at
          ? formatIsoDateToOrgDate(form.status_updated_at, organization)
          : "";

        return !form || !prunedFormTemplate ? (
          <NotFound />
        ) : (
          <Flex direction="column" gap={8}>
            <Breadcrumb
              items={breadcrumb.form.getBreadcrumbsForView(
                organization,
                form,
                prunedFormTemplate,
                searchParams.toString()
              )}
            />
            <AdminHeading
              upperTitle={prunedFormTemplate.enrollment_period.name}
              title={`${fullName(form.person)}'s response`}
              id={id}
              actionButton={
                <Flex>
                  <WithUserPermissions permissions={["form:read"]}>
                    <ExportMenu
                      formData={formData}
                      enrollmentPeriodId={
                        prunedFormTemplate.enrollment_period_id
                      }
                    />
                  </WithUserPermissions>
                </Flex>
              }
              actionBar={
                <Flex gap={2} alignItems="center">
                  <StatusTag status={FORM_STATUS[form.status]} />
                  <Text color="gray.500">
                    {statusUpdatedDate &&
                      `Status last changed: ${statusUpdatedDate}`}
                  </Text>

                  <WithUserPermissions permissions={["form:update"]}>
                    {hasEditStatusPermission && (
                      <>
                        <FormStatusMenu
                          isLoading={isLoading}
                          formStatus={form.status}
                          label="Set to"
                          onSelect={handleOnSelectStatusChange}
                        />
                        <NextStepMenuSingle
                          form={form}
                          formTemplate={prunedFormTemplate}
                        />

                        <SetVisibilityMenuSingle
                          formVisibility={form.is_hidden_from_parent}
                          formId={form.id}
                        />
                      </>
                    )}
                  </WithUserPermissions>
                </Flex>
              }
            />
            <Flex gap={4} justifyContent="flex-end" w="100%">
              <Flex direction="column" flexGrow="1" minWidth="32rem">
                <Tabs defaultIndex={initialTabIndex} isLazy>
                  <TabList>
                    <Tab>
                      <Text textTransform="capitalize">
                        {prunedFormTemplate?.name}
                      </Text>
                    </Tab>
                    {canViewMessages && <Tab>Messages</Tab>}
                    {canViewHistory && <Tab>History</Tab>}
                    {canViewAlerts && (
                      <Tab>
                        {formAlerts.data.length > 0 && (
                          <RiAlertLine style={{ marginRight: "0.5rem" }} />
                        )}
                        Alerts
                        {formAlerts.data.length > 0 &&
                          ` (${formAlerts.data.length})`}
                      </Tab>
                    )}
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      {showBirthdateWarning && (
                        <Alert status="warning" marginBottom="1.5rem">
                          <AlertIcon />
                          <VStack alignItems="flex-start">
                            <Text>
                              Date of birth has changed. Please ensure the
                              following are correct:
                            </Text>
                            <List>
                              <ListItem>- Form grade</ListItem>
                              <ListItem>
                                <Glossary>- School choices</Glossary>
                              </ListItem>
                              <ListItem>
                                <Glossary>- School-specific questions</Glossary>
                              </ListItem>
                            </List>
                          </VStack>
                          <Spacer />
                          <CloseButton onClick={onClose} />
                        </Alert>
                      )}
                      {previousForm && (
                        <FormDetails
                          form={previousForm}
                          assignedForms={assignedForms}
                        />
                      )}
                      <WithRequiredHasuraRoles roles={[HasuraRole.ADMIN]}>
                        <Flex direction="column" gap="2" marginTop="4">
                          <Heading as="h3" variant="admin">
                            Avela admin only
                          </Heading>
                          <FormControl>
                            <FormLabel
                              display="flex"
                              alignItems="center"
                              gap="2"
                            >
                              <Checkbox
                                isChecked={showHiddenQuestions}
                                onChange={(event) => {
                                  setShowHiddenQuestions(event.target.checked);
                                }}
                              />{" "}
                              Show hidden questions
                            </FormLabel>
                          </FormControl>
                        </Flex>
                      </WithRequiredHasuraRoles>
                      {sections.map((section) =>
                        section ? (
                          <Section
                            showHiddenQuestions={showHiddenQuestions}
                            previousFormSchoolIds={previousFormSchoolIds}
                            key={section.id}
                            form={form}
                            allSections={sections}
                            section={section}
                            form_answers={form_answers}
                            grades_answers={grades_answers}
                            address_answers={address_answers}
                            custom_question_answers={custom_question_answers}
                            custom_question_answer_bank_relationships={
                              custom_question_answer_bank_relationships
                            }
                            formDisclaimer={formDisclaimer}
                            schoolsRank={schoolsRank}
                            tagGroups={tagGroups}
                            onEdit={setEditedSectionId}
                            onCloseEdit={async () => {
                              setEditedSectionId(undefined);
                              try {
                                await apolloClient.query({
                                  query: GET_FORM_ANSWERS_BY_ID,
                                  variables: { form_id: id ?? "" },
                                  fetchPolicy: "network-only",
                                });
                              } catch (error) {
                                console.error(
                                  "[View] refetchFormAnswers failed",
                                  error
                                );
                              }
                            }}
                            onRefetchSchoolsRank={async () => {
                              await refetchSchoolsRank?.();
                            }}
                            onRefetchFormAlerts={async () => {
                              await formAlertsQuery.refetch();
                            }}
                            editMode={
                              editedSectionId === undefined
                                ? "view"
                                : editedSectionId === section.id
                                ? "edit"
                                : "disabled"
                            }
                          />
                        ) : null
                      )}
                    </TabPanel>

                    {canViewMessages && (
                      <TabPanel>
                        <FormMessages
                          form={form}
                          formTemplate={prunedFormTemplate}
                        />
                      </TabPanel>
                    )}
                    {canViewHistory && (
                      <TabPanel>
                        <HistoryLog formId={form.id} />
                      </TabPanel>
                    )}
                    {canViewAlerts && (
                      <TabPanel>
                        <FormAlerts
                          alerts={formAlerts}
                          onRetry={(
                            alert: GetFormAlertsByFormIdResponse["data"][number]
                          ) => {
                            // placeholder; logic for refreshing a school grade alert is tbd.
                            // geocoding alerts cannot be refreshed.
                            if (
                              alert.category === "rule" &&
                              alert.subcategory === "invalid-school-grade"
                            )
                              executeFormRulesMutation.mutate();
                          }}
                          isRetrying={
                            executeFormRulesMutation.isPending ||
                            formAlertsQuery.isRefetching
                          }
                          organization={organization}
                          enrollmentPeriodId={
                            prunedFormTemplate.enrollment_period_id
                          }
                          formTemplateId={prunedFormTemplate.id}
                          formId={id}
                        />
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              </Flex>
              <Flex direction="column" gap={4} maxWidth="35%">
                <StudentProfile form={form} formTemplate={prunedFormTemplate} />
                {previousForm && <RelatedForms form={previousForm} />}
                <VerificationSummary
                  verificationSummary={verificationSummary}
                  sections={sections}
                  form_answers={form_answers}
                  grades_answers={grades_answers}
                  address_answers={address_answers}
                  custom_question_answers={custom_question_answers}
                  custom_question_answer_bank_relationships={
                    custom_question_answer_bank_relationships
                  }
                  rankedSchoolIds={schoolsRank.map((rank) => rank.school.id)}
                  previousFormSchoolIds={previousFormSchoolIds}
                />
                <SectionNavigation sections={sections} />
              </Flex>
            </Flex>

            {confirmationDialog}
          </Flex>
        );
      }}
    </RemoteDataView>
  );
};
